import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/9pYpmBqnTFtudjrgYsyKLh/METRO-BRANDBOOK_Mesa_de_trabajo_1_copia_11.svg';
/* ------------------------------------------------------------------ */
/*  Shell: enforces A3 ratio + padding + 3-row column layout           */
/* ------------------------------------------------------------------ */
function PosterFrame({
  children,
  background = '#FFFFFF',
  textColor = '#0E0E12'
}: {
  children: React.ReactNode;
  background?: string;
  textColor?: string;
}) {
  return <div className="rounded-sm overflow-hidden border border-[var(--divider)] shadow-md" style={{
    aspectRatio: '210/297',
    background,
    color: textColor
  }} data-id="element-1255">
      <div className="w-full h-full flex flex-col" style={{
      padding: '5.5%'
    }} data-id="element-1256">
        {children}
      </div>
    </div>;
}
function PosterEyebrow({
  label,
  index,
  dark = false
}: {
  label: string;
  index: string;
  dark?: boolean;
}) {
  return <div className="flex items-center justify-between font-mono" style={{
    fontSize: '6px',
    letterSpacing: '0.25em',
    color: dark ? 'rgba(255,255,255,0.55)' : '#6B6E78',
    flexShrink: 0
  }} data-id="element-1257">
      <span data-id="element-1258">METRO AGENCY · BARCELONA</span>
      <span data-id="element-1259">
        {label} · A3 · {index}
      </span>
    </div>;
}
function PosterFooter({
  category,
  dark = false,
  cta,
  ctaDark = false
}: {
  category: string;
  dark?: boolean;
  cta?: string;
  ctaDark?: boolean;
}) {
  return <div className="flex items-end justify-between" style={{
    borderTop: '1px solid ' + (dark ? 'rgba(255,255,255,0.25)' : 'rgba(14,14,18,0.15)'),
    paddingTop: '10px',
    flexShrink: 0,
    marginTop: '10px'
  }} data-id="element-1260">
      <img src={METRO_LOGO_URL} alt="me—tro" className="h-3.5 w-auto" style={{
      filter: dark ? 'brightness(0) invert(1)' : 'none',
      opacity: dark ? 0.9 : 1
    }} data-id="element-1261" />
      {cta ? <div className={'flex items-center gap-1.5 px-3 py-1 rounded-full font-space text-[9px] font-bold ' + (ctaDark ? 'bg-[var(--text-ink)] text-white' : 'bg-white text-[var(--text-ink)]')} data-id="element-1262">
          {cta}
          <ArrowRightIcon className="w-2.5 h-2.5" strokeWidth={2.5} data-id="element-1263" />
        </div> : <div className="text-right" data-id="element-1264">
          <div className="font-mono" style={{
        fontSize: '6px',
        letterSpacing: '0.25em',
        opacity: dark ? 0.6 : 0.55
      }} data-id="element-1265">
            METRO.STUDIO
          </div>
          <div className="font-mono font-bold mt-0.5" style={{
        fontSize: '6.5px',
        letterSpacing: '0.25em'
      }} data-id="element-1266">
            {category}
          </div>
        </div>}
    </div>;
}
function PriceFrom({
  price,
  suffix,
  color = '#1A2BFF',
  mutedColor = '#6B6E78',
  size = 22
}: {
  price: string;
  suffix?: string;
  color?: string;
  mutedColor?: string;
  size?: number;
}) {
  return <div className="inline-flex items-baseline gap-1.5" data-id="element-1267">
      <span className="font-mono" style={{
      fontSize: '7px',
      letterSpacing: '0.25em',
      color: mutedColor
    }} data-id="element-1268">
        FROM
      </span>
      <span className="font-space font-bold leading-none tracking-tight" style={{
      fontSize: size + 'px',
      color
    }} data-id="element-1269">
        {price}
      </span>
      {suffix && <span className="font-mono" style={{
      fontSize: '8px',
      color: mutedColor
    }} data-id="element-1270">
          {suffix}
        </span>}
    </div>;
}
function PosterCaption({
  number,
  title,
  use,
  format
}: {
  number: string;
  title: string;
  use: string;
  format: string;
}) {
  return <div className="mt-3" data-id="element-1271">
      <div className="flex items-center justify-between mb-1" data-id="element-1272">
        <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-1273">
          {number}
        </div>
        <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-1274">
          {format}
        </div>
      </div>
      <div className="font-space text-base font-medium leading-tight tracking-tight" data-id="element-1275">
        {title}
      </div>
      <div className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed mt-1" data-id="element-1276">
        {use}
      </div>
    </div>;
}
/* ================================================================== */
/*  11.1.1 — NETWORK MAP                                               */
/* ================================================================== */
function PosterNetworkMap() {
  const lines = [{
    color: '#1A2BFF',
    label: 'L1',
    stations: ['Brand', 'Voice', 'Strategy']
  }, {
    color: '#F26A35',
    label: 'L2',
    stations: ['Film', 'Photo', 'Sound']
  }, {
    color: '#2A8E47',
    label: 'L3',
    stations: ['SEO', 'Paid', 'CRO']
  }, {
    color: '#E8C547',
    label: 'L4',
    stations: ['Social', 'Email', 'CRM']
  }, {
    color: '#F4A523',
    label: 'L5',
    stations: ['Web', 'Events', 'Commerce']
  }];
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-1277">
      <PosterEyebrow label="NETWORK" index="01/11" data-id="element-1278" />

      {/* Headline */}
      <div className="mt-[7%]" style={{
      flexShrink: 0
    }} data-id="element-1279">
        <div className="font-space font-bold leading-[0.86] tracking-tight" style={{
        fontSize: '46px'
      }} data-id="element-1280">
          A network
          <br data-id="element-1281" />
          of{' '}
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-1282">
            ideas.
          </span>
        </div>
      </div>

      {/* Map fills available space */}
      <div className="flex-1 min-h-0 mt-[6%] flex items-center" style={{
      marginRight: '-2%'
    }} data-id="element-1283">
        <svg viewBox="0 0 280 220" preserveAspectRatio="xMidYMid meet" className="w-full h-full" data-id="element-1284">
          {lines.map((line, lineIdx) => {
          const y = 22 + lineIdx * 42;
          const count = line.stations.length;
          const startX = 28;
          const endX = 260;
          const stepX = (endX - startX) / (count - 1);
          return <g key={lineIdx} data-id="element-1285">
                <line x1={startX} y1={y} x2={endX} y2={y} stroke={line.color} strokeWidth="4" strokeLinecap="round" data-id="element-1286" />
                <text x={0} y={y + 2} fontFamily="JetBrains Mono, monospace" fontSize="6" fontWeight="800" fill={line.color} letterSpacing="0.5" data-id="element-1287">
                  {line.label}
                </text>
                {line.stations.map((s, i) => {
              const x = startX + stepX * i;
              return <g key={i} data-id="element-1288">
                      <circle cx={x} cy={y} r="4.5" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="1.5" data-id="element-1289" />
                      <text x={x} y={y - 10} fontFamily="'Space Grotesk', sans-serif" fontSize="6.5" fontWeight="700" fill="#0E0E12" textAnchor="middle" data-id="element-1290">
                        {s}
                      </text>
                    </g>;
            })}
              </g>;
        })}
        </svg>
      </div>

      {/* Subline */}
      <div className="font-helvetica leading-snug" style={{
      fontSize: '10px',
      color: '#6B6E78',
      maxWidth: '70%',
      flexShrink: 0
    }} data-id="element-1291">
        Five lines. Fifteen stations. One agency.
      </div>

      <PosterFooter category="NETWORK · BCN" data-id="element-1292" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.1.2 — SERVICE MENU                                              */
/* ================================================================== */
function PosterServiceMenu() {
  const services = [{
    n: '01',
    c: '#1A2BFF',
    name: 'Brand',
    price: '€1,200'
  }, {
    n: '02',
    c: '#E8C547',
    name: 'Website',
    price: '€390'
  }, {
    n: '03',
    c: '#F26A35',
    name: 'Content',
    price: '€350'
  }, {
    n: '04',
    c: '#2A8E47',
    name: 'Performance',
    price: '€350/mo'
  }, {
    n: '05',
    c: '#F4A523',
    name: 'Social',
    price: '€450/mo'
  }, {
    n: '06',
    c: '#0E0E12',
    name: '360°',
    price: 'Custom'
  }];
  return <PosterFrame background="#EDEEF1" textColor="#0E0E12" data-id="element-1293">
      <PosterEyebrow label="MENU" index="02/11" data-id="element-1294" />

      <div className="mt-[6%]" style={{
      flexShrink: 0
    }} data-id="element-1295">
        <div className="font-space font-bold leading-[0.86] tracking-tight" style={{
        fontSize: '34px'
      }} data-id="element-1296">
          One team.
          <br data-id="element-1297" />
          Every craft.
        </div>
      </div>

      {/* Menu list */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-[5%]" data-id="element-1298">
        {services.map(s => <div key={s.n} className="grid items-center border-b border-[var(--text-ink)]" style={{
        gridTemplateColumns: '24px 1fr auto',
        padding: '8px 0',
        gap: '10px'
      }} data-id="element-1299">
            <div className="flex items-center gap-1.5" data-id="element-1300">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
            background: s.c
          }} data-id="element-1301" />
            </div>
            <div className="min-w-0" data-id="element-1302">
              <div className="font-space font-bold tracking-tight leading-none" style={{
            fontSize: '17px'
          }} data-id="element-1303">
                {s.name}
              </div>
              <div className="font-mono mt-1" style={{
            fontSize: '6px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-1304">
                {s.n}
              </div>
            </div>
            <div className="text-right flex-shrink-0" data-id="element-1305">
              <div className="font-mono" style={{
            fontSize: '6px',
            letterSpacing: '0.25em',
            color: '#6B6E78'
          }} data-id="element-1306">
                FROM
              </div>
              <div className="font-space font-bold tracking-tight leading-none mt-0.5" style={{
            fontSize: '15px',
            color: '#1A2BFF'
          }} data-id="element-1307">
                {s.price}
              </div>
            </div>
          </div>)}
      </div>

      <PosterFooter category="MENU · BCN" cta="Start a project" ctaDark data-id="element-1308" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.1.3 — FIVE PILLARS                                              */
/* ================================================================== */
function PosterFivePillars() {
  return <PosterFrame background="#0E0E12" textColor="#FFFFFF" data-id="element-1309">
      <PosterEyebrow label="PILLARS" index="03/11" dark data-id="element-1310" />

      <div className="mt-[6%]" style={{
      flexShrink: 0
    }} data-id="element-1311">
        <div className="font-space font-bold leading-[0.86] tracking-tight" style={{
        fontSize: '36px'
      }} data-id="element-1312">
          Strategy
          <br data-id="element-1313" />
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-1314">
            to screen.
          </span>
        </div>
        <div className="font-helvetica mt-3 leading-snug" style={{
        fontSize: '10px',
        color: 'rgba(255,255,255,0.7)',
        maxWidth: '85%'
      }} data-id="element-1315">
          No silos. No passing the buck.
        </div>
      </div>

      {/* Pillar grid — fills space */}
      <div className="flex-1 min-h-0 mt-[5%] grid grid-cols-2 gap-1.5" data-id="element-1316">
        {[{
        n: '01',
        t: 'Brand',
        p: '€1,200',
        bg: '#1A2BFF',
        fg: '#FFFFFF'
      }, {
        n: '02',
        t: 'Web',
        p: '€390',
        bg: '#E8C547',
        fg: '#0E0E12'
      }, {
        n: '03',
        t: 'Content',
        p: '€350',
        bg: '#F26A35',
        fg: '#FFFFFF'
      }, {
        n: '04',
        t: 'Perform',
        p: '€350/MO',
        bg: '#2A8E47',
        fg: '#FFFFFF'
      }].map(p => <div key={p.n} className="p-2.5 flex flex-col justify-between min-w-0" style={{
        background: p.bg,
        color: p.fg
      }} data-id="element-1317">
            <div className="font-mono font-bold" style={{
          fontSize: '7px',
          letterSpacing: '0.2em',
          opacity: 0.8
        }} data-id="element-1318">
              {p.n}
            </div>
            <div data-id="element-1319">
              <div className="font-space font-bold leading-none tracking-tight" style={{
            fontSize: '20px'
          }} data-id="element-1320">
                {p.t}
              </div>
              <div className="font-mono font-bold mt-2" style={{
            fontSize: '7px',
            letterSpacing: '0.18em',
            opacity: 0.9
          }} data-id="element-1321">
                FROM {p.p}
              </div>
            </div>
          </div>)}
        {/* Wide 360 block */}
        <div className="col-span-2 p-3 flex items-center justify-between min-w-0" style={{
        background: '#EDEEF1',
        color: '#0E0E12'
      }} data-id="element-1322">
          <div className="min-w-0" data-id="element-1323">
            <div className="font-mono font-bold" style={{
            fontSize: '7px',
            letterSpacing: '0.2em',
            opacity: 0.7
          }} data-id="element-1324">
              05
            </div>
            <div className="font-space font-bold leading-none tracking-tight mt-1" style={{
            fontSize: '20px'
          }} data-id="element-1325">
              360° Marketing
            </div>
          </div>
          <div className="text-right flex-shrink-0 ml-3" data-id="element-1326">
            <div className="font-mono" style={{
            fontSize: '6.5px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-1327">
              ALL OF THE ABOVE
            </div>
            <div className="font-mono font-bold mt-1" style={{
            fontSize: '8px',
            letterSpacing: '0.2em',
            color: '#1A2BFF'
          }} data-id="element-1328">
              CUSTOM
            </div>
          </div>
        </div>
      </div>

      <PosterFooter category="PILLARS · BCN" dark cta="Start a project" data-id="element-1329" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.1 — WEB DESIGN                                                */
/* ================================================================== */
function PosterWebDesign() {
  return <PosterFrame background="#1A2BFF" textColor="#FFFFFF" data-id="element-1330">
      <PosterEyebrow label="WEB" index="04/11" dark data-id="element-1331" />

      {/* Browser card */}
      <div className="mt-[6%] bg-white rounded-sm overflow-hidden text-[var(--text-ink)]" style={{
      flexShrink: 0,
      boxShadow: '0 18px 48px rgba(0,0,0,0.25)'
    }} data-id="element-1332">
        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#F4F5F7] border-b border-[var(--divider)]" data-id="element-1333">
          <div className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" data-id="element-1334" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" data-id="element-1335" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#28C840]" data-id="element-1336" />
          <div className="flex-1 ml-1.5 bg-white rounded-sm px-2 py-0.5 font-mono text-[6px] text-[var(--text-muted)]" data-id="element-1337">
            yourbrand.com
          </div>
        </div>
        <div className="p-3" data-id="element-1338">
          <div className="font-mono" style={{
          fontSize: '5px',
          letterSpacing: '0.3em',
          color: '#1A2BFF'
        }} data-id="element-1339">
            WELCOME ━━━━━
          </div>
          <div className="font-space font-bold mt-2 leading-[0.9] tracking-tight" style={{
          fontSize: '14px'
        }} data-id="element-1340">
            Hi. I'm
            <br data-id="element-1341" />
            yourbrand.
          </div>
          <div className="mt-2.5 inline-block bg-[var(--text-ink)] text-white rounded-full px-2 py-0.5 font-mono font-bold" style={{
          fontSize: '5px',
          letterSpacing: '0.15em'
        }} data-id="element-1342">
            GET IN TOUCH →
          </div>
        </div>
      </div>

      {/* Headline + price block */}
      <div className="flex-1 min-h-0 flex flex-col justify-end" data-id="element-1343">
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '34px'
      }} data-id="element-1344">
          Your website
          <br data-id="element-1345" />
          is your first
          <br data-id="element-1346" />
          hello.
        </div>
        <div className="font-helvetica mt-3 leading-snug" style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.85)'
      }} data-id="element-1347">
          Make it count.
        </div>
        <div className="mt-3" data-id="element-1348">
          <PriceFrom price="€990" color="#FFFFFF" mutedColor="rgba(255,255,255,0.7)" size={26} data-id="element-1349" />
        </div>
      </div>

      <PosterFooter category="WEB · BCN" dark data-id="element-1350" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.2 — BRANDING                                                  */
/* ================================================================== */
function PosterBranding() {
  return <PosterFrame background="#EDEEF1" textColor="#0E0E12" data-id="element-1351">
      <PosterEyebrow label="BRAND" index="05/11" data-id="element-1352" />

      {/* Color palette strip — fixed height */}
      <div className="mt-[5%]" style={{
      flexShrink: 0
    }} data-id="element-1353">
        <div className="flex h-2.5" data-id="element-1354">
          {['#1A2BFF', '#E8C547', '#F26A35', '#2A8E47', '#F4A523', '#0E0E12'].map((c, i) => <div key={i} className="flex-1" style={{
          background: c
        }} data-id="element-1355" />)}
        </div>
        <div className="flex font-mono mt-1.5" style={{
        fontSize: '5px',
        color: '#6B6E78',
        letterSpacing: '0.18em'
      }} data-id="element-1356">
          {['P 286 C', 'P 7404', 'P 165 C', 'P 7740', 'P 137 C', 'INK'].map((c, i) => <div key={i} className="flex-1 text-center" data-id="element-1357">
                {c}
              </div>)}
        </div>
      </div>

      {/* Giant M — contained block */}
      <div className="flex-1 min-h-0 flex items-center justify-center mt-[2%]" style={{
      overflow: 'hidden'
    }} data-id="element-1358">
        <div className="font-space font-bold leading-none tracking-[-0.05em] text-center" style={{
        fontSize: '180px',
        color: '#1A2BFF'
      }} data-id="element-1359">
          M
        </div>
      </div>

      {/* Headline + price */}
      <div style={{
      flexShrink: 0
    }} data-id="element-1360">
        <div className="font-space font-bold leading-[0.9] tracking-tight" style={{
        fontSize: '22px'
      }} data-id="element-1361">
          Your brand speaks
          <br data-id="element-1362" />
          before you do.
        </div>
        <div className="mt-3 flex items-end justify-between gap-3" data-id="element-1363">
          <div className="font-helvetica leading-snug" style={{
          fontSize: '10px',
          color: '#6B6E78',
          maxWidth: '60%'
        }} data-id="element-1364">
            Logo, palette, type, guidelines.
          </div>
          <PriceFrom price="€1,200" size={22} data-id="element-1365" />
        </div>
      </div>

      <PosterFooter category="BRAND · BCN" data-id="element-1366" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.3 — VIDEO & PHOTOGRAPHY                                       */
/* ================================================================== */
function PosterVideoPhoto() {
  return <PosterFrame background="#0E0E12" textColor="#FFFFFF" data-id="element-1367">
      <PosterEyebrow label="STUDIO" index="06/11" dark data-id="element-1368" />

      {/* REC bar */}
      <div className="flex items-center gap-2 mt-2" style={{
      flexShrink: 0
    }} data-id="element-1369">
        <div className="w-1.5 h-1.5 rounded-full bg-[#F26A35]" data-id="element-1370" />
        <span className="font-mono font-bold" style={{
        fontSize: '7px',
        letterSpacing: '0.25em',
        color: '#F26A35'
      }} data-id="element-1371">
          REC
        </span>
        <span className="font-mono" style={{
        fontSize: '7px',
        letterSpacing: '0.25em',
        opacity: 0.6
      }} data-id="element-1372">
          00:00:30:00 · 4K
        </span>
      </div>

      {/* Film frame */}
      <div className="flex-1 min-h-0 relative border border-white/15 mt-[5%] flex flex-col items-center justify-center px-3 text-center" style={{
      overflow: 'hidden'
    }} data-id="element-1373">
        {/* Corner crops */}
        {[{
        top: '4px',
        left: '4px'
      }, {
        top: '4px',
        right: '4px'
      }, {
        bottom: '4px',
        left: '4px'
      }, {
        bottom: '4px',
        right: '4px'
      }].map((pos, i) => <div key={i} className="absolute" style={{
        ...pos,
        width: '10px',
        height: '10px'
      }} data-id="element-1374">
            <div className="absolute top-0 left-0 w-full h-px bg-white/40" data-id="element-1375" />
            <div className="absolute top-0 left-0 h-full w-px bg-white/40" data-id="element-1376" />
          </div>)}

        <div className="font-mono mb-3" style={{
        fontSize: '5.5px',
        letterSpacing: '0.4em',
        opacity: 0.55
      }} data-id="element-1377">
          ━━━━━ FRAME 01 ━━━━━
        </div>
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '34px'
      }} data-id="element-1378">
          Every frame
          <br data-id="element-1379" />
          earns its
          <br data-id="element-1380" />
          <span style={{
          color: '#F26A35'
        }} data-id="element-1381">
            place.
          </span>
        </div>
      </div>

      {/* Subline + price */}
      <div className="mt-[4%]" style={{
      flexShrink: 0
    }} data-id="element-1382">
        <div className="font-helvetica leading-snug" style={{
        fontSize: '11px',
        opacity: 0.85
      }} data-id="element-1383">
          Built for purpose.
        </div>
        <div className="mt-2" data-id="element-1384">
          <PriceFrom price="€350" color="#F26A35" mutedColor="rgba(255,255,255,0.6)" size={22} data-id="element-1385" />
        </div>
      </div>

      <PosterFooter category="STUDIO · BCN" dark data-id="element-1386" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.4 — SOCIAL MEDIA                                              */
/* ================================================================== */
function PosterSocialMedia() {
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-1387">
      <PosterEyebrow label="SOCIAL" index="07/11" data-id="element-1388" />

      {/* Story progress bars */}
      <div className="flex gap-1 mt-2" style={{
      flexShrink: 0
    }} data-id="element-1389">
        {[100, 100, 60, 0, 0].map((p, i) => <div key={i} className="flex-1 h-0.5 bg-[var(--divider)] rounded-full overflow-hidden" data-id="element-1390">
            <div className="h-full" style={{
          width: p + '%',
          background: '#0E0E12'
        }} data-id="element-1391" />
          </div>)}
      </div>

      {/* Decorative post grid — fixed height ratio */}
      <div className="mt-2 grid grid-cols-3 gap-1" style={{
      flexShrink: 0,
      aspectRatio: '3/2'
    }} data-id="element-1392">
        {['#1A2BFF', '#EDEEF1', '#F26A35', '#E8C547', '#0E0E12', '#2A8E47'].map((b, i) => <div key={i} style={{
        background: b
      }} data-id="element-1393" />)}
      </div>

      {/* Q&A in flex region */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-[4%]" data-id="element-1394">
        <div className="flex items-start gap-2.5" data-id="element-1395">
          <span className="font-mono font-bold flex-shrink-0" style={{
          fontSize: '16px',
          color: '#1A2BFF'
        }} data-id="element-1396">
            Q.
          </span>
          <span className="font-space font-bold leading-[0.92] tracking-tight" style={{
          fontSize: '22px'
        }} data-id="element-1397">
            Who posts
            <br data-id="element-1398" />
            for you?
          </span>
        </div>
        <div className="flex items-start gap-2.5 mt-3 pl-5" data-id="element-1399">
          <span className="font-mono font-bold flex-shrink-0" style={{
          fontSize: '16px',
          color: '#1A2BFF'
        }} data-id="element-1400">
            A.
          </span>
          <span className="font-space font-bold leading-[0.92] tracking-tight" style={{
          fontSize: '32px',
          color: '#1A2BFF'
        }} data-id="element-1401">
            We do.
          </span>
        </div>
      </div>

      {/* Price block */}
      <div className="mt-3" style={{
      flexShrink: 0
    }} data-id="element-1402">
        <PriceFrom price="€450" suffix="/ month" size={22} data-id="element-1403" />
      </div>

      <PosterFooter category="SOCIAL · BCN" data-id="element-1404" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.5 — MARKETING 360°                                            */
/* ================================================================== */
function PosterMarketing360() {
  return <PosterFrame background="#EDEEF1" textColor="#0E0E12" data-id="element-1405">
      <PosterEyebrow label="360°" index="08/11" data-id="element-1406" />

      {/* Map area — flex 1 */}
      <div className="flex-1 min-h-0 mt-[5%] relative" data-id="element-1407">
        <svg viewBox="0 0 280 360" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full" data-id="element-1408">
          <path d="M -10 60 L 80 60 L 140 120 L 290 120" stroke="#1A2BFF" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-1409" />
          <path d="M -10 130 L 60 130 L 100 90 L 200 90 L 240 130 L 290 130" stroke="#E8C547" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-1410" />
          <path d="M -10 200 L 100 200 L 160 260 L 290 260" stroke="#F26A35" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-1411" />
          <path d="M -10 290 L 80 290 L 130 340 L 290 340" stroke="#2A8E47" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-1412" />
          <path d="M 60 -10 L 60 290 L 130 340" stroke="#F4A523" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-1413" />
          {[[80, 60], [140, 120], [60, 130], [100, 90], [200, 90], [240, 130], [100, 200], [160, 260], [80, 290], [130, 340], [60, 60]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="5" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="1.8" data-id="element-1414" />)}
        </svg>

        {/* Black overlay band centered */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2" data-id="element-1415">
          <div className="bg-[var(--text-ink)] text-white mx-2 px-3 py-3" data-id="element-1416">
            <div className="font-mono mb-1.5" style={{
            fontSize: '6px',
            letterSpacing: '0.3em',
            opacity: 0.7
          }} data-id="element-1417">
              MARKETING 360°
            </div>
            <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
            fontSize: '30px'
          }} data-id="element-1418">
              Not a menu.
              <br data-id="element-1419" />A{' '}
              <span style={{
              color: '#1A2BFF'
            }} data-id="element-1420">
                system.
              </span>
            </div>
            <div className="font-helvetica leading-snug mt-2" style={{
            fontSize: '9px',
            opacity: 0.85
          }} data-id="element-1421">
              Strategy. Creative. Production. One team.
            </div>
          </div>
        </div>
      </div>

      <PosterFooter category="360° · BCN" data-id="element-1422" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.6 — MICRO / LOCAL                                             */
/* ================================================================== */
function PosterMicroPack() {
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-1423">
      <PosterEyebrow label="LOCAL" index="09/11" data-id="element-1424" />

      {/* Search bar */}
      <div className="mt-[5%]" style={{
      flexShrink: 0
    }} data-id="element-1425">
        <div className="flex items-center gap-2 border border-[var(--divider)] rounded-full px-3 py-1.5 bg-white" data-id="element-1426">
          <SearchIcon className="w-2.5 h-2.5 text-[var(--text-muted)]" data-id="element-1427" />
          <span className="font-helvetica flex-1 truncate" style={{
          fontSize: '8px',
          color: '#6B6E78'
        }} data-id="element-1428">
            your business · barcelona
          </span>
          <span className="font-mono" style={{
          fontSize: '6px',
          letterSpacing: '0.25em',
          color: '#6B6E78'
        }} data-id="element-1429">
            G
          </span>
        </div>
        <div className="mt-2 font-mono" style={{
        fontSize: '6px',
        letterSpacing: '0.25em',
        color: '#6B6E78'
      }} data-id="element-1430">
          ABOUT 0 RESULTS · (0.00 SECONDS)
        </div>
        <div className="mt-1.5 h-px bg-[var(--divider)]" data-id="element-1431" />
        <div className="mt-1.5 space-y-1" data-id="element-1432">
          {[1, 2].map(i => <div key={i} className="space-y-0.5 opacity-25" data-id="element-1433">
              <div className="h-0.5 bg-[var(--divider)] rounded-full w-1/2" data-id="element-1434" />
              <div className="h-0.5 bg-[var(--divider)] rounded-full w-2/3" data-id="element-1435" />
            </div>)}
        </div>
      </div>

      {/* Headline */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-[4%]" data-id="element-1436">
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '30px'
      }} data-id="element-1437">
          Your business
          <br data-id="element-1438" />
          is not on
          <br data-id="element-1439" />
          <span style={{
          color: '#1A2BFF',
          textDecoration: 'line-through',
          textDecorationColor: '#0E0E12',
          textDecorationThickness: '2px'
        }} data-id="element-1440">
            Google.
          </span>
        </div>
        <div className="font-space font-bold mt-3" style={{
        fontSize: '18px'
      }} data-id="element-1441">
          We fix that.
        </div>
        <div className="font-helvetica mt-2 leading-snug" style={{
        fontSize: '10px',
        color: '#6B6E78'
      }} data-id="element-1442">
          One page. 72 hours. Done.
        </div>
      </div>

      {/* Price + locations */}
      <div className="mt-[3%]" style={{
      flexShrink: 0
    }} data-id="element-1443">
        <div className="flex items-baseline justify-between gap-2 border-t border-[var(--text-ink)] pt-2" data-id="element-1444">
          <PriceFrom price="€390" size={36} data-id="element-1445" />
          <span className="font-mono" style={{
          fontSize: '6px',
          letterSpacing: '0.22em',
          color: '#6B6E78'
        }} data-id="element-1446">
            BCN · SBD · TRS
          </span>
        </div>
      </div>

      <PosterFooter category="LOCAL · BCN" cta="Start a project" ctaDark data-id="element-1447" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.7 — SEO & PERFORMANCE                                         */
/* ================================================================== */
function PosterSEO() {
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-1448">
      <PosterEyebrow label="SEO" index="10/11" data-id="element-1449" />

      {/* Headline */}
      <div className="mt-[5%]" style={{
      flexShrink: 0
    }} data-id="element-1450">
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '24px'
      }} data-id="element-1451">
          Page 2 of
          <br data-id="element-1452" />
          Google is
          <br data-id="element-1453" />
          where brands
          <br data-id="element-1454" />
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-1455">
            go to hide.
          </span>
        </div>
      </div>

      {/* SERP stacks */}
      <div className="flex-1 min-h-0 mt-[5%] flex flex-col justify-center gap-3" data-id="element-1456">
        {/* Page 1 */}
        <div data-id="element-1457">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-1458">
            <div className="font-mono font-bold px-2 py-0.5 rounded-full" style={{
            background: '#1A2BFF',
            color: '#FFFFFF',
            fontSize: '6px',
            letterSpacing: '0.25em'
          }} data-id="element-1459">
              PAGE 1
            </div>
            <div className="font-mono" style={{
            fontSize: '6px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-1460">
              ← visible
            </div>
          </div>
          {['yourbrand.com', 'yourbrand.com/services'].map(u => <div key={u} className="mb-1 border-l-2 pl-2 py-0.5" style={{
          borderColor: '#1A2BFF'
        }} data-id="element-1461">
              <div className="font-mono" style={{
            fontSize: '6px',
            color: '#6B6E78'
          }} data-id="element-1462">
                {u}
              </div>
              <div className="font-space font-bold leading-tight" style={{
            fontSize: '10px',
            color: '#1A2BFF'
          }} data-id="element-1463">
                Yourbrand — Welcome.
              </div>
            </div>)}
        </div>

        {/* Page 2 */}
        <div className="opacity-30" data-id="element-1464">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-1465">
            <div className="font-mono font-bold px-2 py-0.5 rounded-full border border-[var(--text-ink)]" style={{
            fontSize: '6px',
            letterSpacing: '0.25em'
          }} data-id="element-1466">
              PAGE 2
            </div>
            <div className="font-mono" style={{
            fontSize: '6px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-1467">
              ← invisible
            </div>
          </div>
          {['competitor.com'].map(u => <div key={u} className="mb-1 pl-2.5 py-0.5" data-id="element-1468">
              <div className="font-mono" style={{
            fontSize: '6px',
            color: '#6B6E78'
          }} data-id="element-1469">
                {u}
              </div>
              <div className="font-space font-bold leading-tight" style={{
            fontSize: '10px'
          }} data-id="element-1470">
                Competitor — home
              </div>
            </div>)}
        </div>
      </div>

      {/* Subline + price */}
      <div className="border-t border-[var(--text-ink)] pt-2 mt-[3%] flex items-baseline justify-between gap-3" style={{
      flexShrink: 0
    }} data-id="element-1471">
        <div className="font-space font-bold leading-tight tracking-tight" style={{
        fontSize: '13px',
        maxWidth: '55%'
      }} data-id="element-1472">
          We get you found.
        </div>
        <PriceFrom price="€350" suffix="/ month" size={20} data-id="element-1473" />
      </div>

      <PosterFooter category="SEO · BCN" data-id="element-1474" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.8 — EVENTS & STREAMING                                        */
/* ================================================================== */
function PosterEvents() {
  return <PosterFrame background="#0E0E12" textColor="#FFFFFF" data-id="element-1475">
      <PosterEyebrow label="EVENTS" index="11/11" dark data-id="element-1476" />

      {/* LIVE bar */}
      <div className="flex items-center justify-between mt-2" style={{
      flexShrink: 0
    }} data-id="element-1477">
        <div className="flex items-center gap-2" data-id="element-1478">
          <div className="px-1.5 py-0.5 rounded-sm font-mono font-bold flex items-center gap-1" style={{
          background: '#F26A35',
          color: '#FFFFFF',
          fontSize: '7px',
          letterSpacing: '0.25em'
        }} data-id="element-1479">
            <div className="w-1 h-1 bg-white rounded-full animate-pulse" data-id="element-1480" />
            LIVE
          </div>
          <span className="font-mono" style={{
          fontSize: '6.5px',
          letterSpacing: '0.2em',
          opacity: 0.7
        }} data-id="element-1481">
            1,247 WATCHING
          </span>
        </div>
        <span className="font-mono" style={{
        fontSize: '6.5px',
        letterSpacing: '0.2em',
        opacity: 0.7
      }} data-id="element-1482">
          1080P · 60FPS
        </span>
      </div>

      {/* Stream window */}
      <div className="mt-[5%] border border-white/20 relative" style={{
      flexShrink: 0,
      aspectRatio: '16/9'
    }} data-id="element-1483">
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-1484">
          <div className="font-space font-bold tracking-tight text-center" style={{
          fontSize: '40px',
          lineHeight: 0.85,
          background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.35) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }} data-id="element-1485">
            ON
            <br data-id="element-1486" />
            AIR
          </div>
        </div>
        <div className="absolute left-2 right-2" style={{
        bottom: '5px'
      }} data-id="element-1487">
          <div className="h-0.5 bg-white/15 rounded-full overflow-hidden" data-id="element-1488">
            <div className="h-full" style={{
            width: '38%',
            background: '#F26A35'
          }} data-id="element-1489" />
          </div>
          <div className="flex justify-between font-mono mt-0.5" style={{
          fontSize: '5px',
          opacity: 0.55
        }} data-id="element-1490">
            <span data-id="element-1491">00:14:22</span>
            <span data-id="element-1492">00:38:00</span>
          </div>
        </div>
      </div>

      {/* Headline + subline */}
      <div className="flex-1 min-h-0 flex flex-col justify-end" data-id="element-1493">
        <div className="font-space font-bold leading-[0.9] tracking-tight" style={{
        fontSize: '24px'
      }} data-id="element-1494">
          Your event
          <br data-id="element-1495" />
          deserves
          <br data-id="element-1496" />
          more than a
          <br data-id="element-1497" />
          phone on a
          <br data-id="element-1498" />
          <span style={{
          color: '#F26A35'
        }} data-id="element-1499">
            tripod.
          </span>
        </div>
        <div className="font-helvetica mt-3 leading-snug" style={{
        fontSize: '10px',
        opacity: 0.85
      }} data-id="element-1500">
          Production. Streaming. One team.
        </div>
      </div>

      <PosterFooter category="EVENTS · BCN" dark data-id="element-1501" />
    </PosterFrame>;
}
/* ================================================================== */
/*  Section                                                            */
/* ================================================================== */
export function PostersSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="posters" ref={ref} className="min-h-screen px-8 py-24" data-id="element-1502">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-1503">
        <div className="mb-16 max-w-3xl" data-id="element-1504">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-1505">
            11 / POSTERS
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-1506">
            Made to be seen.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-1507">
            Eleven posters at A3 portrait. Each one runs on the same skeleton —
            eyebrow, hero, subline, price, footer — so they read as one family.
            Each has a single graphic idea earning the wall: a five-line
            network, a giant blue M, a film slate, a stream UI, a search with
            zero results.
          </p>
        </div>

        {/* General */}
        <div className="mb-20" data-id="element-1508">
          <div className="flex items-baseline justify-between mb-8" data-id="element-1509">
            <div data-id="element-1510">
              <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-2" data-id="element-1511">
                11.1 / GENERAL
              </div>
              <h3 className="font-space text-3xl font-bold tracking-tight" data-id="element-1512">
                Three for any wall.
              </h3>
            </div>
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-1513">
              A3 PORTRAIT · 297 × 420 MM
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1514">
            <div data-id="element-1515">
              <PosterNetworkMap data-id="element-1516" />
              <PosterCaption number="11.1.1" title="Network map" use="Five lines, fifteen stations. The agency as one graphic." format="A3 · CMYK" data-id="element-1517" />
            </div>
            <div data-id="element-1518">
              <PosterServiceMenu data-id="element-1519" />
              <PosterCaption number="11.1.2" title="Service menu" use="Prices visible at a glance. For shop windows and receptions." format="A3 · CMYK" data-id="element-1520" />
            </div>
            <div data-id="element-1521">
              <PosterFivePillars data-id="element-1522" />
              <PosterCaption number="11.1.3" title="Five pillars" use="Full-bleed black with color blocks. High-impact lobby piece." format="A3 · CMYK" data-id="element-1523" />
            </div>
          </div>
        </div>

        {/* Service-specific */}
        <div className="mb-20" data-id="element-1524">
          <div className="flex items-baseline justify-between mb-8" data-id="element-1525">
            <div data-id="element-1526">
              <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-2" data-id="element-1527">
                11.2 / SERVICE-SPECIFIC
              </div>
              <h3 className="font-space text-3xl font-bold tracking-tight" data-id="element-1528">
                Eight, one per service.
              </h3>
            </div>
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-1529">
              A3 PORTRAIT · 297 × 420 MM
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-id="element-1530">
            <div data-id="element-1531">
              <PosterWebDesign data-id="element-1532" />
              <PosterCaption number="11.2.1" title="Web Design" use="The poster IS the website. A browser card sits at the top, headline as the page." format="A3" data-id="element-1533" />
            </div>
            <div data-id="element-1534">
              <PosterBranding data-id="element-1535" />
              <PosterCaption number="11.2.2" title="Branding" use="A giant blue M owning the canvas. Pantones at the top, palette at a glance." format="A3" data-id="element-1536" />
            </div>
            <div data-id="element-1537">
              <PosterVideoPhoto data-id="element-1538" />
              <PosterCaption number="11.2.3" title="Video & Photography" use="Film slate aesthetic. Title card in the safe area. Orange REC accent." format="A3" data-id="element-1539" />
            </div>
            <div data-id="element-1540">
              <PosterSocialMedia data-id="element-1541" />
              <PosterCaption number="11.2.4" title="Social Media" use="Instagram story bars + post grid + Q/A. Native to where the work lives." format="A3" data-id="element-1542" />
            </div>
            <div data-id="element-1543">
              <PosterMarketing360 data-id="element-1544" />
              <PosterCaption number="11.2.5" title="Marketing 360°" use="Full transit map. Black band carries the headline mid-poster." format="A3" data-id="element-1545" />
            </div>
            <div data-id="element-1546">
              <PosterMicroPack data-id="element-1547" />
              <PosterCaption number="11.2.6" title="Local · Micro" use="Fake Google returning zero results. Strike-through. €390." format="A3" data-id="element-1548" />
            </div>
            <div data-id="element-1549">
              <PosterSEO data-id="element-1550" />
              <PosterCaption number="11.2.7" title="SEO & Performance" use="Two SERP stacks. Page 1 bold, Page 2 faded. The argument is the design." format="A3" data-id="element-1551" />
            </div>
            <div data-id="element-1552">
              <PosterEvents data-id="element-1553" />
              <PosterCaption number="11.2.8" title="Events & Streaming" use="Live stream UI on ink black. ON AIR card with scrubber + viewer count." format="A3" data-id="element-1554" />
            </div>
          </div>
        </div>

        {/* Copy rules */}
        <div data-id="element-1555">
          <div className="mb-8" data-id="element-1556">
            <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-2" data-id="element-1557">
              11.3 / COPY RULES
            </div>
            <h3 className="font-space text-3xl font-bold tracking-tight" data-id="element-1558">
              For the posters that come next.
            </h3>
            <p className="text-sm text-[var(--text-muted)] mt-3 max-w-2xl font-helvetica leading-relaxed" data-id="element-1559">
              The system for writing any future Metro poster. Pasted into the
              brief, this gets you a poster that reads from far, lands up close,
              and matches the rest of the wall.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1560">
            {[{
            tag: 'HEADLINE',
            title: '3–8 words',
            body: 'Reads at 5 metres. Space Grotesk Bold. The single thing the viewer should take away if they only see one thing.',
            sample: 'Your website is your first hello.'
          }, {
            tag: 'SUBLINE',
            title: '6–12 words',
            body: 'Reads at 2 metres. Helvetica Neue Regular. Adds the second beat — the offer, the proof, the next step.',
            sample: 'Make it count.'
          }, {
            tag: 'PRICE',
            title: 'Always "From €X"',
            body: 'Honesty. No starred footnotes, no "starting at". If recurring, "from €X / month". Mono keeps it legible at distance.',
            sample: 'from €450 / month'
          }, {
            tag: 'CTA',
            title: 'One line max',
            body: 'Web OR phone. Never both. If a poster needs two CTAs, it needs two posters. Always ending with a period.',
            sample: 'Start a project →'
          }, {
            tag: 'WHITE SPACE',
            title: '70% air',
            body: 'If it looks empty, it is right. Posters compete with everything else on the wall — only the work that needs to land, lands.',
            sample: '— empty space is the design —'
          }, {
            tag: 'PUNCTUATION',
            title: 'Periods. Never exclamations.',
            body: 'Confidence is quiet. The work argues for itself. One exclamation across eleven posters: that is the system.',
            sample: 'We do.  ✗ We do!'
          }].map(r => <div key={r.tag} className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--bg-surface)]" data-id="element-1561">
                <div className="flex items-baseline justify-between mb-3" data-id="element-1562">
                  <span className="font-mono text-[10px] text-[var(--accent-blue)] font-bold tracking-widest" data-id="element-1563">
                    {r.tag}
                  </span>
                  <span className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-1564">
                    RULE
                  </span>
                </div>
                <div className="font-space font-bold text-lg tracking-tight leading-tight mb-2" data-id="element-1565">
                  {r.title}
                </div>
                <p className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed mb-3" data-id="element-1566">
                  {r.body}
                </p>
                <div className="border-l-2 border-[var(--accent-blue)] pl-3 py-1" data-id="element-1567">
                  <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-1568">
                    SAMPLE
                  </div>
                  <div className="font-space text-sm font-medium italic" data-id="element-1569">
                    {r.sample}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Print spec */}
          <div className="mt-8 bg-[var(--text-ink)] text-white rounded-sm p-6 flex items-start gap-6" data-id="element-1570">
            <div className="font-mono text-[9px] tracking-widest text-[var(--accent-blue)] font-bold flex-shrink-0" data-id="element-1571">
              PRINT SPEC
            </div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm" data-id="element-1572">
              <div data-id="element-1573">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-1574">
                  SIZE
                </div>
                <div className="font-space font-bold text-base" data-id="element-1575">
                  A3 · 297 × 420 mm
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-1576">
                  Portrait. Bleed 3 mm.
                </div>
              </div>
              <div data-id="element-1577">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-1578">
                  PAPER
                </div>
                <div className="font-space font-bold text-base" data-id="element-1579">
                  170 g uncoated
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-1580">
                  Matte. No varnish.
                </div>
              </div>
              <div data-id="element-1581">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-1582">
                  COLOUR
                </div>
                <div className="font-space font-bold text-base" data-id="element-1583">
                  CMYK + Pantone
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-1584">
                  Metro Blue: Pantone 286 C.
                </div>
              </div>
              <div data-id="element-1585">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-1586">
                  FILES
                </div>
                <div className="font-space font-bold text-base" data-id="element-1587">
                  PDF/X-1a + AI
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-1588">
                  Fonts outlined for print.
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}